// 简单的加密/解密工具
// 使用Base64 + XOR加密

const ENCRYPTION_KEY = 'KIMAP_TM_2024_SECRET_KEY';

/**
 * 加密数据
 * @param data 要加密的数据对象
 * @returns 加密后的字符串
 */
export function encryptData(data: any): string {
  try {
    // 1. 转换为JSON字符串
    const jsonString = JSON.stringify(data);
    
    // 2. XOR加密
    const encrypted = xorEncrypt(jsonString, ENCRYPTION_KEY);
    
    // 3. UTF-8安全的Base64编码
    const base64 = utf8ToBase64(encrypted);
    
    // 4. 添加标识头
    return `KIMAP_TM_V1:${base64}`;
  } catch (error) {
    console.error('Encryption failed:', error);
    throw new Error('数据加密失败');
  }
}

/**
 * 解密数据
 * @param encryptedString 加密的字符串
 * @returns 解密后的数据对象
 */
export function decryptData(encryptedString: string): any {
  try {
    // 1. 检查标识头
    if (!encryptedString.startsWith('KIMAP_TM_V1:')) {
      throw new Error('无效的加密格式');
    }
    
    // 2. 移除标识头
    const base64 = encryptedString.substring(12);
    
    // 3. UTF-8安全的Base64解码
    const encrypted = base64ToUtf8(base64);
    
    // 4. XOR解密
    const jsonString = xorDecrypt(encrypted, ENCRYPTION_KEY);
    
    // 5. 解析JSON
    return JSON.parse(jsonString);
  } catch (error) {
    console.error('Decryption failed:', error);
    throw new Error('数据解密失败');
  }
}

/**
 * UTF-8字符串转Base64（支持中文等Unicode字符）
 */
function utf8ToBase64(str: string): string {
  // 使用encodeURIComponent处理Unicode字符，然后转换为Base64
  const encoded = encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (_, p1) => {
    return String.fromCharCode(parseInt(p1, 16));
  });
  return btoa(encoded);
}

/**
 * Base64转UTF-8字符串（支持中文等Unicode字符）
 */
function base64ToUtf8(base64: string): string {
  // Base64解码后，再用decodeURIComponent恢复Unicode字符
  const decoded = atob(base64);
  const encoded = decoded.split('').map(char => {
    return '%' + ('00' + char.charCodeAt(0).toString(16)).slice(-2);
  }).join('');
  return decodeURIComponent(encoded);
}

/**
 * XOR加密
 */
function xorEncrypt(text: string, key: string): string {
  let result = '';
  for (let i = 0; i < text.length; i++) {
    const charCode = text.charCodeAt(i) ^ key.charCodeAt(i % key.length);
    result += String.fromCharCode(charCode);
  }
  return result;
}

/**
 * XOR解密（与加密相同）
 */
function xorDecrypt(encrypted: string, key: string): string {
  return xorEncrypt(encrypted, key); // XOR加密是对称的
}

/**
 * 生成文件名
 */
export function generateFileName(projectName: string): string {
  const timestamp = Date.now();
  const safeName = projectName.replace(/[^a-zA-Z0-9\u4e00-\u9fa5]/g, '_');
  return `${safeName}_${timestamp}.kimap.tm`;
}

/**
 * 验证是否为有效的加密文件
 */
export function isValidEncryptedFile(content: string): boolean {
  return content.startsWith('KIMAP_TM_V1:');
}
